import { SearchEmptyState } from "@louise/moon-ui/primitives/SearchEmptyState";
import { useTranslations } from "@src/packages/i18n";
import { useState, useCallback, useMemo, useRef } from "react";
import {
  View,
  Text,
  type TextInput,
  Image,
  ScrollView,
  Modal,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { X, Search, Clock, Trash2 } from "lucide-react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
  FadeIn,
} from "react-native-reanimated";
import { CommonActions, useNavigation } from "@react-navigation/native";
import Fuse from "fuse.js";
import {
  springs,
  haptic,
  CompactActionButton,
  MoonSearchInput,
  IconButton,
  SectionActionLink,
  SupportingSubtitleText,
  cardSurface,
} from "@louise/moon-ui";
import { EMBER } from "../../../onboarding/theme";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import { useSearchItems } from "./useSearchItems";
import { useRecentSearches } from "./useRecentSearches";
import { CATEGORY_CONFIG } from "./searchTypes";
import type { SearchItem, SearchCategory } from "./searchTypes";
import SearchResultRow from "./SearchResultRow";
import { semanticColors } from "@louise/theme/tokens";

type Props = {
  visible: boolean;
  onClose: () => void;
};

function SearchContent({ onClose }: { onClose: () => void }) {
  const t = useTranslations();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const searchItems = useSearchItems();
  const { recents, addRecent, clearRecents } = useRecentSearches();
  const [query, setQuery] = useState("");
  const inputRef = useRef<TextInput>(null);

  // ── Animation ──
  const scale = useSharedValue(0.92);
  const opacity = useSharedValue(0);

  useState(() => {
    scale.value = withSpring(1, springs.sheet);
    opacity.value = withTiming(1, { duration: 250 });
    haptic("light");
  });

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    overflow: "hidden" as const,
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const handleClose = useCallback(() => {
    scale.value = withSpring(0.92, springs.sheet);
    opacity.value = withTiming(0, { duration: 200 }, () => {
      runOnJS(onClose)();
    });
  }, [scale, opacity, onClose]);

  // ── Fuse.js search ──
  const fuse = useMemo(
    () =>
      new Fuse(searchItems, {
        keys: [
          { name: "title", weight: 3 },
          { name: "subtitle", weight: 1.5 },
          { name: "keywords", weight: 1 },
        ],
        threshold: 0.4,
        includeScore: true,
        minMatchCharLength: 2,
      }),
    [searchItems],
  );

  const results = useMemo(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) return [];
    return fuse.search(trimmed, { limit: 50 });
  }, [fuse, query]);

  // ── Group results by category ──
  const grouped = useMemo(() => {
    const groups = new Map<
      SearchCategory,
      { items: SearchItem[]; bestScore: number }
    >();

    for (const result of results) {
      const cat = result.item.category;
      const existing = groups.get(cat);
      const score = result.score ?? 1;
      if (existing) {
        existing.items.push(result.item);
        if (score < existing.bestScore) existing.bestScore = score;
      } else {
        groups.set(cat, { items: [result.item], bestScore: score });
      }
    }

    // Sort groups by best score, with priority tiebreaker. Grants always last.
    return Array.from(groups.entries())
      .sort((a, b) => {
        // Grants always sink to the bottom
        if (a[0] === "grants" && b[0] !== "grants") return 1;
        if (b[0] === "grants" && a[0] !== "grants") return -1;

        const scoreDiff = a[1].bestScore - b[1].bestScore;
        if (Math.abs(scoreDiff) < 0.05) {
          return (
            CATEGORY_CONFIG[a[0]].priority - CATEGORY_CONFIG[b[0]].priority
          );
        }
        return scoreDiff;
      })
      .map(([category, { items }]) => ({ category, items }));
  }, [results]);

  // ── Navigation on result tap ──
  const handleResultPress = useCallback(
    (item: SearchItem) => {
      haptic("light");
      addRecent(query);

      if (!item.navTarget) {
        handleClose();
        return;
      }

      const { tab, screen, params } = item.navTarget;

      // Dispatch from the tab navigator (parent) so the tab name resolves
      // to the outer Tab.Screen and not to a colliding inner Stack.Screen
      // (e.g. the Learn tab contains a Learn stack screen with the same
      // name — dispatching from the current stack would swallow the nested
      // { screen, params } and leave the user on the tab's initial route).
      const navAction =
        screen && screen !== tab
          ? CommonActions.navigate(tab, {
              screen,
              ...(params ? { params } : {}),
            })
          : CommonActions.navigate(tab);

      const target = navigation.getParent() ?? navigation;

      // Close first, then navigate after the animation plays
      // (same pattern as NotificationCenterSheet)
      handleClose();
      setTimeout(() => {
        target.dispatch(navAction);
      }, 250);
    },
    [navigation, handleClose, addRecent, query],
  );

  const handleRecentPress = useCallback((recent: string) => {
    setQuery(recent);
    inputRef.current?.focus();
  }, []);

  const hasQuery = query.trim().length >= 2;
  const hasResults = grouped.length > 0;

  return (
    <View style={{ flex: 1, overflow: "hidden" }}>
      {/* Background image */}
      <Image
        source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
        style={{
          position: "absolute",
          width: "115%",
          height: "115%",
          left: "-7.5%",
          top: "-7.5%",
        }}
        resizeMode="cover"
      />

      <Animated.View style={animatedStyle}>
        <View style={{ flex: 1, paddingTop: insets.top }}>
          <ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Glass card */}
            <View
              style={{
                ...cardSurface(),
                paddingHorizontal: 20,
                paddingTop: 20,
                paddingBottom: 20,
              }}
            >
              {/* Close button */}
              <IconButton
                onPress={handleClose}
                hitSlop={8}
                size={32}
                surface="soft"
                backgroundColor={semanticColors.surface.muted}
                pressedBackgroundColor={semanticColors.surface.muted}
                pressedOpacity={1}
                accessibilityLabel="Close"
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                }}
              >
                <X size={16} color={EMBER.colors.textLight} />
              </IconButton>

              {/* Title */}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 20,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                  marginBottom: 16,
                  paddingRight: 40,
                }}
              >
                Search
              </Text>

              {/* Search input */}
              <MoonSearchInput
                ref={inputRef}
                value={query}
                onChangeText={setQuery}
                placeholder="Search My Louise..."
                autoFocus
                returnKeyType="search"
                onClear={() => setQuery("")}
                style={{ marginBottom: 16 }}
              />

              {/* ── Pre-search: Recent searches ── */}
              {!hasQuery && recents.length > 0 && (
                <Animated.View entering={FadeIn.duration(200)}>
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 10,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        fontWeight: "600",
                        color: EMBER.colors.textMid,
                      }}
                    >
                      Recent
                    </Text>
                    <SectionActionLink
                      label="Clear"
                      icon={Trash2}
                      showChevron={false}
                      onPress={clearRecents}
                    />
                  </View>

                  <View
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    {recents.map((recent) => (
                      <CompactActionButton
                        key={recent}
                        label={recent}
                        action="recent_search"
                        variant="secondary"
                        size="sm"
                        leadingIcon={Clock}
                        onPress={() => handleRecentPress(recent)}
                      />
                    ))}
                  </View>
                </Animated.View>
              )}

              {/* ── Pre-search: Hint ── */}
              {!hasQuery && recents.length === 0 && (
                <View style={{ alignItems: "center", paddingVertical: 32 }}>
                  <Search
                    size={32}
                    color={EMBER.colors.textFaint}
                    strokeWidth={1}
                  />
                  <SupportingSubtitleText
                    style={{
                      textAlign: "center",
                      marginTop: 12,
                    }}
                  >
                    {t("empty_states.search_across_your_care")}
                  </SupportingSubtitleText>
                </View>
              )}

              {/* ── No results ── */}
              {hasQuery && !hasResults && (
                <Animated.View
                  entering={FadeIn.duration(200)}
                  style={{
                    alignItems: "center",
                    paddingVertical: 32,
                  }}
                >
                  <SearchEmptyState
                    title={t("empty_states.no_results_for_search", {
                      params: { query: query.trim() },
                    })}
                    onReset={() => setQuery("")}
                    style={{ paddingVertical: 0 }}
                  />
                </Animated.View>
              )}

              {/* ── Grouped results ── */}
              {hasQuery && hasResults && (
                <View style={{ gap: 20 }}>
                  {grouped.map(({ category, items }, groupIdx) => {
                    const config = CATEGORY_CONFIG[category];
                    const Icon = config.icon;
                    return (
                      <Animated.View
                        key={category}
                        entering={FadeIn.duration(200).delay(groupIdx * 60)}
                      >
                        {/* Section header */}
                        <View
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 4,
                          }}
                        >
                          <Icon
                            size={14}
                            color={config.color}
                            strokeWidth={1.5}
                          />
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 12,
                              fontWeight: "600",
                              color: EMBER.colors.textMid,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            {config.label}
                          </Text>
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 11,
                              color: EMBER.colors.textFaint,
                            }}
                          >
                            {items.length}
                          </Text>
                        </View>

                        {/* Items */}
                        {items.map((item, idx) => (
                          <SearchResultRow
                            key={item.id}
                            item={item}
                            index={idx}
                            onPress={handleResultPress}
                          />
                        ))}
                      </Animated.View>
                    );
                  })}
                </View>
              )}
            </View>
          </ScrollView>
        </View>
      </Animated.View>
    </View>
  );
}

export default function SearchSheet({ visible, onClose }: Props) {
  if (!visible) return null;

  return (
    <Modal visible transparent animationType="none" onRequestClose={onClose}>
      <SearchContent onClose={onClose} />
    </Modal>
  );
}
